import {
  Lead,
  leadStatusSchema,
  objectIdSchema,
  paginationWithSearchSchema,
  ReferralPartner,
  ReferralPartnerSchema,
  referralPartnerTypeSchema,
  statusCommonSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';

/* ─────────────────────────── admin ─────────────────────────── */

/**
 * `GET /referral/partner/admin-all` — paginated + searchable list of
 * every `ReferralPartner`.
 */
export const referralPartnerGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  status: statusCommonSchema
    .optional()
    .describe(
      'Filter by `ReferralPartner.status` (`active` / `blocked` / `inactive` / `pending`).',
    ),
  type: referralPartnerTypeSchema
    .optional()
    .describe('Filter by partner type (`marketer` / `customer` / `employee` / `other`).'),
  ownerId: objectIdSchema
    .optional()
    .describe('Filter to the partner whose `owner._id` is this account (Org / User / Driver).'),
});
export type ReferralPartnerGetAllAdminAPI = API<
  z.infer<typeof referralPartnerGetAllAdminAPISchema>,
  ReferralPartner[]
>;

/** `GET /referral/partner/admin-one` — one partner by id. */
export const referralPartnerGetOneAdminAPISchema = z.object({
  _id: objectIdSchema.describe('`ReferralPartner._id`.'),
});
export type ReferralPartnerGetOneAdminAPI = API<
  z.infer<typeof referralPartnerGetOneAdminAPISchema>,
  ReferralPartner
>;

/**
 * `POST /referral/partner/admin-create` — create a partner. `reward`
 * is the standing deal every link inherits; leave `parts` empty for a
 * tracking-only partner.
 */
export const referralPartnerCreateAdminAPISchema = ReferralPartnerSchema.data
  .omit({ status: true })
  .extend({
    status: statusCommonSchema.optional().describe('Defaults to `active`.'),
  });
export type ReferralPartnerCreateAdminAPI = API<
  z.infer<typeof referralPartnerCreateAdminAPISchema>,
  ReferralPartner
>;

/** `PATCH /referral/partner/admin-update` — partial update by id. */
export const referralPartnerUpdateAdminAPISchema = ReferralPartnerSchema.partial.extend({
  _id: objectIdSchema.describe('`ReferralPartner._id` to update.'),
});
export type ReferralPartnerUpdateAdminAPI = API<
  z.infer<typeof referralPartnerUpdateAdminAPISchema>,
  ReferralPartner
>;

/**
 * The referral scoreboard. Every number is a **query over the source of
 * truth** at read time (`ReferralTracking`, `User`, `Org`, `Lead`) — not
 * a counter — so a lead that moves to `won` in the CRM is a won lead
 * here too. Ownership is by the link the person **converted through**
 * (`meta.referral` on User / Org, `meta.sourceMeta.referral` on Lead).
 *
 * Definitions (mirrored in the admin panel's info tooltips):
 * - `views` / `clicks` — `ReferralTracking` hits on the scope's links,
 *   deduped per browser by the tracking endpoint.
 * - `uniqueVisitors` — distinct `visitorId`s (browsers / devices) across
 *   those hits, on every surface (landing, dashboard, apps, external
 *   sites). A partner's total is distinct across its links, so it can be
 *   less than the sum of the per-link numbers.
 * - `orgs` — `Org`s created through this scope (`Org.meta.referral`).
 * - `leads` — `Lead` rows attributed to this scope, any mode.
 * - `signedUp` — those leads that created an account (`account_created`
 *   funnel step or a party on the lead). Shown as "Users (signed up)":
 *   a signup and a user are the same thing to a partner.
 * - `activated` — those leads with a first order or first waybill.
 * - `paid` — those leads with a settled Billing (`paid` step).
 * - `won` — leads whose CRM status is `won`.
 * - `lost` — leads whose CRM status is `unqualified`, `lost` or `dead`.
 */
export type ReferralMetrics = {
  views: number;
  clicks: number;
  uniqueVisitors: number;
  orgs: number;
  leads: number;
  signedUp: number;
  activated: number;
  paid: number;
  won: number;
  lost: number;
};
export const REFERRAL_METRIC_KEYS = [
  'views',
  'clicks',
  'uniqueVisitors',
  'orgs',
  'leads',
  'signedUp',
  'activated',
  'paid',
  'won',
  'lost',
] as const satisfies ReadonlyArray<keyof ReferralMetrics>;
export const emptyReferralMetrics = (): ReferralMetrics => ({
  views: 0,
  clicks: 0,
  uniqueVisitors: 0,
  orgs: 0,
  leads: 0,
  signedUp: 0,
  activated: 0,
  paid: 0,
  won: 0,
  lost: 0,
});

/**
 * `GET /referral/partner/admin-summary` — the partner's scoreboard:
 * totals, the same metrics per link, and reward totals by status.
 */
export const referralPartnerSummaryAdminAPISchema = z.object({
  _id: objectIdSchema.describe('`ReferralPartner._id`.'),
});
export type ReferralPartnerSummary = {
  partnerId: string;
  links: number;
  activeLinks: number;
  totals: ReferralMetrics;
  /** One row per link, active or not, so a paused link keeps its history. */
  perLink: Array<{ linkId: string; code: string } & ReferralMetrics>;
  rewards: {
    total: number;
    pending: number;
    granted: number;
    void: number;
    /** Cash owed and not yet paid, per currency. */
    pendingAmount: Partial<Record<'SAR' | 'USD', number>>;
    /** Cash paid out, per currency. */
    grantedAmount: Partial<Record<'SAR' | 'USD', number>>;
    /** Credits granted, per business. */
    grantedUnits: Partial<Record<'hero' | 'wathiqa_plus', number>>;
  };
};
export type ReferralPartnerSummaryAdminAPI = API<
  z.infer<typeof referralPartnerSummaryAdminAPISchema>,
  ReferralPartnerSummary
>;

/**
 * `GET /referral/lead/admin-all` — the leads a partner (or one of its
 * links) owns, newest first. Ownership is `Lead.meta.sourceMeta.referral`
 * — the link the person converted through. Same `Lead` rows the CRM
 * shows, so the admin can jump straight into the lead drawer.
 */
export const referralLeadGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  partnerId: objectIdSchema.optional().describe('Scope to a partner.'),
  linkId: objectIdSchema.optional().describe('Scope to one link.'),
  status: z
    .union([leadStatusSchema, z.array(leadStatusSchema)])
    .optional()
    .describe('Filter to one or more CRM statuses.'),
});
export type ReferralLeadGetAllAdminAPI = API<
  z.infer<typeof referralLeadGetAllAdminAPISchema>,
  Lead[]
>;
